import { RatesAndRestrictionsInterface } from '@components/calendar/reservation-calendar';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Moment } from 'moment';
import { GridType } from '../../../modules/front-office/interface/fo-grids-api.interface';

/**
 * Merge date-based items into target array.
 * Updates existing dates or pushes new ones.
 */
const mergeDateItems = <T extends { date: string }>(
  target: T[] = [],
  source: T[] = [],
  valueKey: keyof T,
) => {
  source.forEach((src) => {
    const idx = target.findIndex((t) => t.date === src.date);

    if (idx !== -1) {
      target[idx][valueKey] = src[valueKey];
    } else {
      target.push(src);
    }
  });

  return target;
};

export interface FOConfigurationState {
  calendar: {
    views: {
      userID: string;
      view: GridType;
      date: Moment | null;
    }[];
  };
  modalsData: RatesAndRestrictionsInterface[];
}

const initialState: FOConfigurationState = {
  calendar: {
    views: [],
  },
  modalsData: [],
};

export const FOConfigurationSlice = createSlice({
  name: 'foConfiguration',
  initialState,
  reducers: {
    setCalendarView: (
      state,
      action: PayloadAction<FOConfigurationState['calendar']['views'][0]>,
    ) => {
      const { userID, date, view: payloadView } = action.payload;
      const view = state.calendar.views?.find((view) => view.userID === userID);

      if (view) {
        view.date = date;
        view.view = payloadView;
      } else {
        state.calendar.views.push(action.payload);
      }
    },
    setCalendarViewDate: (
      state,
      action: PayloadAction<{ date: Moment | null; userID: string }>,
    ) => {
      const { date, userID } = action.payload;
      const view = state.calendar.views?.find((view) => view.userID === userID);
      if (view && date) {
        view.date = date;
      }
    },
    removeCalendarView: (state, action: PayloadAction<string>) => {
      state.calendar.views = state.calendar.views.filter(
        (view) => view.userID !== action.payload,
      );
    },
    addModalData: (
      state,
      action: PayloadAction<RatesAndRestrictionsInterface[]>,
    ) => {
      action.payload.forEach((incoming) => {
        const existing = state.modalsData?.find(
          (item) =>
            item.ratePlan === incoming.ratePlan &&
            item.roomType === incoming.roomType &&
            item.restriction === incoming.restriction,
        );

        if (!existing) {
          if (Array.isArray(state.modalsData)) {
            state.modalsData.push(incoming);
            return;
          }
          state.modalsData = [incoming];
          return;
        }

        mergeDateItems(existing.cta, incoming.cta, 'status');
        mergeDateItems(existing.ctd, incoming.ctd, 'status');
        mergeDateItems(existing.stopSell, incoming.stopSell, 'status');

        mergeDateItems(existing.maxStay, incoming.maxStay, 'value');
        mergeDateItems(existing.minStay, incoming.minStay, 'value');
        mergeDateItems(existing.buffer, incoming.buffer, 'value');
        mergeDateItems(existing.maxSell, incoming.maxSell, 'value');
        mergeDateItems(existing.minSell, incoming.minSell, 'value');
        mergeDateItems(existing.overbooked, incoming.overbooked, 'value');
      });
    },

    clearModalData: (state) => {
      state.modalsData = [];
    },
  },
});

export default FOConfigurationSlice.reducer;

// Add all of the reducers to the export below
export const {
  setCalendarView,
  setCalendarViewDate,
  removeCalendarView,
  addModalData,
  clearModalData,
} = FOConfigurationSlice.actions;
